import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { fetchAnalytics, fetchChart, fetchLedger, fetchSeasons } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { CumulativeChart } from "@/components/results/CumulativeChart";
import { ResultsStats, PerformanceBreakdown } from "@/components/results/ResultsStats";
import { LedgerTable } from "@/components/results/LedgerTable";

function useResultsData(season, result, market) {
  const params = { season: season || undefined };
  const analytics = useQuery({ queryKey: ["analytics", season], queryFn: () => fetchAnalytics(params), placeholderData: keepPreviousData });
  const chart = useQuery({ queryKey: ["chart", season], queryFn: () => fetchChart(params), placeholderData: keepPreviousData });
  const ledger = useQuery({
    queryKey: ["ledger", season, result, market],
    queryFn: () => fetchLedger({ ...params, result: result || undefined, market: market || undefined, limit: 100 }),
    placeholderData: keepPreviousData,
  });
  return { analytics, chart, ledger };
}

function DataModeBadge({ demo, overall }) {
  if (demo) return <span data-testid="results-demo-badge" className="tag border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">Demo data · real tracking starts at launch</span>;
  return (
    <div className="flex flex-wrap gap-2">
      <span data-testid="results-live-badge" className="tag border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]">Official ledger · real settled bets</span>
      {overall?.historical_count > 0 && <span data-testid="results-historical-note" className="tag border border-slate-500/40 bg-slate-500/10 text-slate-300" title="Some slips were recorded manually from the owner's betting account with supporting screenshots; website picks are settled automatically from final box scores">{overall.historical_count} manually recorded · {overall.website_count} auto-verified</span>}
    </div>
  );
}

function SeasonSelect({ season, setSeason, seasons }) {
  if (!seasons?.length) return null;
  return (
    <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-slate-400">
      Season
      <select data-testid="results-season-select" value={season} onChange={(e) => setSeason(e.target.value)} className="min-h-[40px] rounded-md border border-[#2A364F] bg-[#0A0D14] px-2 py-1 text-base text-slate-200 outline-none focus:border-[#00E676] sm:min-h-0 sm:text-[11px]">
        {seasons.map((s) => <option key={s} value={s}>{s.replace("-", "–")} NBA</option>)}
      </select>
    </label>
  );
}

export default function Results() {
  const [result, setResult] = useState("");
  const [market, setMarket] = useState("");
  const [season, setSeasonState] = useState("");
  const seasons = useQuery({ queryKey: ["seasons"], queryFn: fetchSeasons });
  const activeSeason = season || seasons.data?.current || "";
  const { analytics, chart, ledger } = useResultsData(activeSeason, result, market);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-20 lg:px-8" data-testid="results-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionHeader
          eyebrow="Public Ledger"
          title="Track The Results. Not The Hype."
          sub="Every bet we post and track for the community is shown here — wins, losses, and all."
          testid="results-header"
        />
        <div className="mb-10 flex flex-col items-end gap-3 sm:mb-14">
          <SeasonSelect season={activeSeason} setSeason={setSeasonState} seasons={seasons.data?.seasons} />
          {analytics.data && <DataModeBadge demo={analytics.data.demo} overall={analytics.data.overall} />}
        </div>
      </div>

      <ResultsStats overall={analytics.data?.overall} isLoading={analytics.isLoading} />
      <CumulativeChart points={chart.data?.points} />
      <PerformanceBreakdown analytics={analytics.data} />
      <LedgerTable
        rows={ledger.data?.rows}
        isLoading={ledger.isLoading}
        isFetching={ledger.isFetching}
        filters={{ result, setResult, market, setMarket }}
      />
      <p className="mt-4 font-mono text-[10px] leading-relaxed text-slate-600" data-testid="results-footnote">
        Unit reference: 1U = ${analytics.data?.overall?.unit_stake_aud ?? 100} AUD. Net profit = returns − stakes; ROI = net ÷ stakes; win rate excludes pushes. A multi counts as one slip. Pending picks are excluded until settled.
        Rows marked "manually recorded" were placed and shared before the website's tracker existed and are recorded from the owner's betting account with screenshots; website picks are verified automatically from final box scores. Dates marked * are game dates where the placement time was not established.
      </p>
    </div>
  );
}
